import React, { useEffect, useState } from 'react';
import emailjs from '@emailjs/browser';
import { emailjsConfig } from '../../config/emailjs';
import {
  CONTACT,
  SKILLS,
  TRAINING,
  EVALS,
  LIMITATIONS,
  CHANGELOG,
} from '../../data/portfolio';

const About: React.FC = () => {
  const [clock, setClock] = useState('');
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<'idle' | 'ok' | 'err'>('idle');
  const [error, setError] = useState('');

  useEffect(() => {
    const tick = () => {
      setClock(
        new Date().toLocaleTimeString('en-US', {
          timeZone: 'America/New_York',
          hour12: false,
        })
      );
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setStatus('idle');
    setError('');
    try {
      if (
        emailjsConfig.serviceId.startsWith('your_') ||
        emailjsConfig.templateId.startsWith('your_') ||
        emailjsConfig.publicKey.startsWith('your_')
      ) {
        throw new Error('EmailJS is not configured yet.');
      }
      await emailjs.send(
        emailjsConfig.serviceId,
        emailjsConfig.templateId,
        {
          from_name: form.name,
          from_email: form.email,
          subject: form.subject,
          message: form.message,
          to_email: CONTACT.email,
        },
        emailjsConfig.publicKey
      );
      setStatus('ok');
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      setStatus('err');
      setError(err instanceof Error ? err.message : 'Failed to send. Try email instead.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page">
      <div className="cardhead">
        <div className="portrait-frame">
          <div className="portrait">
            <img src={CONTACT.photo} alt="Sahil Chordia" />
          </div>
        </div>
        <div className="cardtitle">
          <h2>sahil-chordia</h2>
          <p className="ver">v2.5 · model card · last updated 2025</p>
          <span className="pill">
            <i />
            available for work
          </span>
        </div>
      </div>

      <div className="sec">
        <h3 className="label">capabilities</h3>
        <div className="cap-groups">
          {SKILLS.map((group) => (
            <div key={group.level} className="cap-group">
              <div className="cap-head">
                <span className="cap-level">{group.level}</span>
                <span className="cap-blurb">{group.blurb}</span>
              </div>
              <div className="cap-tags">
                {group.items.map((item) => (
                  <span key={item} className="cap-tag">
                    {item}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="sec">
        <h3 className="label">training data</h3>
        <ul className="plainlist">
          {TRAINING.map((t) => (
            <li key={t}>{t}</li>
          ))}
        </ul>
      </div>

      <div className="sec">
        <h3 className="label">evals</h3>
        <table className="evals">
          <thead>
            <tr>
              <th>benchmark</th>
              <th>result</th>
            </tr>
          </thead>
          <tbody>
            {EVALS.map(([b, r]) => (
              <tr key={b}>
                <td>{b}</td>
                <td>{r}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="sec">
        <h3 className="label">known limitations</h3>
        <ul className="plainlist soft">
          {LIMITATIONS.map((l) => (
            <li key={l}>{l}</li>
          ))}
        </ul>
      </div>

      <div className="sec">
        <h3 className="label">changelog</h3>
        {CHANGELOG.map(([v, d, txt]) => (
          <div key={v} className="log">
            <span className="v">{v}</span>
            <span className="d">{d}</span>
            <span>{txt}</span>
          </div>
        ))}
      </div>

      <div className="contact">
        <h3>Let&apos;s build something.</h3>
        <div className="clinks">
          <a href={`mailto:${CONTACT.email}`}>
            <svg viewBox="0 0 24 24">
              <rect x="2" y="5" width="20" height="14" rx="2" />
              <path d="m3 7 9 6 9-6" />
            </svg>
            <span>email</span>
          </a>
          <a href={CONTACT.github} target="_blank" rel="noopener noreferrer">
            <svg viewBox="0 0 24 24">
              <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.9a3.4 3.4 0 0 0-.9-2.6c3-.3 6.2-1.5 6.2-6.7A5.2 5.2 0 0 0 19.9 5a4.9 4.9 0 0 0-.1-3.6s-1.1-.3-3.7 1.4a12.7 12.7 0 0 0-6.7 0C6.8 1.1 5.7 1.4 5.7 1.4A4.9 4.9 0 0 0 5.6 5a5.2 5.2 0 0 0-1.4 3.6c0 5.2 3.1 6.4 6.1 6.7a3.4 3.4 0 0 0-.9 2.6V22" />
            </svg>
            <span>github</span>
          </a>
          <a href={CONTACT.linkedin} target="_blank" rel="noopener noreferrer">
            <svg viewBox="0 0 24 24">
              <rect x="2" y="2" width="20" height="20" rx="2" />
              <path d="M7 10v7M7 7v.01M11.5 17v-4a2.5 2.5 0 0 1 5 0v4" />
            </svg>
            <span>linkedin</span>
          </a>
          <a href={CONTACT.resume} download>
            <svg viewBox="0 0 24 24">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
            </svg>
            <span>resume</span>
          </a>
        </div>

        <form className="contact-form" onSubmit={onSubmit}>
          <p className="label" style={{ marginBottom: 16 }}>
            or send a message
          </p>
          {status === 'ok' && <div className="form-msg ok">Message sent. I&apos;ll get back to you soon.</div>}
          {status === 'err' && <div className="form-msg err">{error}</div>}
          <div className="grid2">
            <div className="field">
              <label htmlFor="c-name">Name</label>
              <input
                id="c-name"
                required
                disabled={submitting}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor="c-email">Email</label>
              <input
                id="c-email"
                type="email"
                required
                disabled={submitting}
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
          </div>
          <div className="field">
            <label htmlFor="c-subject">Subject</label>
            <input
              id="c-subject"
              required
              disabled={submitting}
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
            />
          </div>
          <div className="field">
            <label htmlFor="c-message">Message</label>
            <textarea
              id="c-message"
              required
              disabled={submitting}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
            />
          </div>
          <button type="submit" className="submit" disabled={submitting}>
            {submitting ? 'sending…' : 'send message'}
          </button>
        </form>

        <p className="footline">
          based in {CONTACT.location.toLowerCase()} · my local time{' '}
          <span className="cy">{clock} et</span>
        </p>
      </div>
    </div>
  );
};

export default About;
